import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import WarningIcon from "@mui/icons-material/Warning"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography
} from "@mui/material"
import { useMemo } from "react"
import { CallAdminLogStep } from "__generated__/graphql"

interface Props {
  correlationId: string
  steps: CallAdminLogStep[]
  onStepClick: (step: CallAdminLogStep) => void
}

const STATUS_COLORS = {
  success: "#10b981",
  failed: "#ef4444",
  skipped: "#6b7280",
  completed: "#10b981",
  started: "#3b82f6"
}

export default function LogGroup({ correlationId, steps, onStepClick }: Props) {
  const parent = useMemo(
    () =>
      steps.find((s) => s.type === "webhook" && s.status === "started") ||
      steps[0],
    [steps]
  )
  const completion = useMemo(
    () => steps.find((s) => s.type === "webhook" && s.status !== "started"),
    [steps]
  )
  const audits = useMemo(
    () =>
      steps
        .filter((s) => s.type === "audit")
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        ),
    [steps]
  )

  const finalStatus = completion?.status || parent.status
  const duration = completion?.duration
  const statusColor =
    STATUS_COLORS[finalStatus as keyof typeof STATUS_COLORS] || "#64748b"

  return (
    <Accordion defaultExpanded sx={{ mb: 1, border: "1px solid #e2e8f0" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          mr={2}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {parent.stepName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {correlationId}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {duration !== undefined && duration !== null && (
              <Chip label={`${duration}ms`} size="small" variant="outlined" />
            )}
            <Chip
              label={finalStatus}
              size="small"
              sx={{
                bgcolor: statusColor,
                color: "white",
                textTransform: "capitalize"
              }}
            />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: "#f8fafc", p: 0 }}>
        <List disablePadding>
          {parent && (
            <StepRow
              step={parent}
              onClick={() => onStepClick(parent)}
              isWebhook
            />
          )}
          {audits.map((audit, index) => (
            <StepRow
              key={index}
              step={audit}
              onClick={() => onStepClick(audit)}
            />
          ))}
          {completion && (
            <StepRow
              step={completion}
              onClick={() => onStepClick(completion)}
              isWebhook
            />
          )}
          {audits.length === 0 && !parent && !completion && (
            <Box p={2}>
              <Typography variant="body2" color="text.secondary">
                No logs recorded for this step.
              </Typography>
            </Box>
          )}
        </List>
      </AccordionDetails>
    </Accordion>
  )
}

function StepRow({
  step,
  onClick,
  isWebhook = false
}: {
  step: CallAdminLogStep
  onClick: () => void
  isWebhook?: boolean
}) {
  const statusColor =
    STATUS_COLORS[step.status as keyof typeof STATUS_COLORS] || "#64748b"

  const getIcon = (status: string) => {
    if (isWebhook) {
      return <FiberManualRecordIcon sx={{ color: statusColor, fontSize: 20 }} />
    }
    switch (status) {
      case "success":
        return <CheckCircleIcon sx={{ color: statusColor, fontSize: 18 }} />
      case "failed":
        return <ErrorIcon sx={{ color: statusColor, fontSize: 18 }} />
      case "skipped":
        return <WarningIcon sx={{ color: statusColor, fontSize: 22 }} />
      default:
        return (
          <FiberManualRecordIcon sx={{ color: statusColor, fontSize: 14 }} />
        )
    }
  }

  return (
    <ListItem
      button
      onClick={onClick}
      sx={{
        borderBottom: "1px solid #f1f5f9",
        "&:last-child": { borderBottom: "none" },
        pl: isWebhook ? 2 : 4,
        bgcolor: isWebhook ? "rgba(59, 130, 246, 0.04)" : "transparent"
      }}
    >
      <ListItemIcon sx={{ minWidth: 30 }}>{getIcon(step.status)}</ListItemIcon>
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              fontWeight={isWebhook ? "bold" : "normal"}
            >
              {step.stepName} {isWebhook ? `(${step.status})` : ""}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              - {new Date(step.timestamp).toLocaleTimeString()}
            </Typography>
          </Stack>
        }
      />
    </ListItem>
  )
}
